import React, { forwardRef } from 'react'
import { Select, SelectProps } from 'antd'
import { AntdFormItem, AntdFormItemProps } from '@/common/components/AntdForm/AntdFormItem.tsx'
import { cn } from '@/utils/style.ts'
import { SelectGroupOption, SelectOption } from '@/common/types/components.def.ts'
import { CurrencyOptions } from '@/common/types/compensation.def.ts'
import { AntdFormRules } from '@/common/components/AntdForm/rules.tsx'

const filterOption = (input: string, option: {
  label: string
}) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

export const CustomSelect: React.FC<React.ComponentProps<typeof Select>> = forwardRef((props, ref) => {
  const showSearch = props.showSearch ?? true
  return (
    <Select
      {...props}
      showSearch={showSearch}
      filterOption={showSearch ? filterOption as SelectProps['filterOption'] : undefined}
      ref={ref}
      allowClear={props.allowClear ?? true}
      className={cn('w-full', props.className)}
    />
  )
})

type AntdFormSelectProps = React.ComponentProps<typeof Select> & {
  options: (SelectOption | SelectGroupOption) []
} & AntdFormItemProps

type AntdFormPredefinedSelectProps = React.ComponentProps<typeof Select> & AntdFormItemProps

export const AntdFormSelect: React.FC<AntdFormSelectProps> = forwardRef(({
  fieldProps,
  options,
  ...props
}, ref) => {
  const finalOptions = props.mode === 'tags' ? [{
    label: 'Input or Select',
    value: undefined,
    disabled: true,
  }, ...options] : options
  const rulesMap = {
    selectSubset: props.mode === 'multiple' || !props.mode,
    maxCount: props.maxCount === undefined ? -1 : props.maxCount,
  }
  const finalRules = [
    ...fieldProps.rules ?? []
    , ...(rulesMap.selectSubset ? [AntdFormRules.validSelectedValue(options)] : [])
    , ...(rulesMap.maxCount >= 0 ? [AntdFormRules.maxItems(rulesMap.maxCount)] : []),
  ]

  return (
    <AntdFormItem
      {...fieldProps} rules={finalRules}
      extra={fieldProps.extra ?? (props.mode === 'tags' ? 'Input or select values' : undefined)}
      afterLabel={fieldProps.afterLabel ?? (rulesMap.maxCount >= 0 ? ` (Max. ${rulesMap.maxCount})` : undefined)}
    >
      <CustomSelect ref={ref} {...props} options={finalOptions} />
    </AntdFormItem>
  )
})

// @ts-ignore
export const AntdFormCurrency: React.FC<AntdFormPredefinedSelectProps> = forwardRef(({
  fieldProps,
  className,
  showSearch = true,
  ...props
}, ref) => {
  return (
    <AntdFormItem {...fieldProps}>
      <Select
        {...props}
        showSearch={showSearch}
        filterOption={showSearch ? filterOption as SelectProps['filterOption'] : undefined}
        ref={ref}
        allowClear={props.allowClear ?? true}
        className={cn('w-full', className)}
        options={CurrencyOptions}
      />
    </AntdFormItem>
  )
})

export const AntdFormMonth: React.FC<AntdFormPredefinedSelectProps> = forwardRef(({
  fieldProps,
  className,
  showSearch = true,
  ...props
}, ref) => {
  return (
    <AntdFormItem {...fieldProps}>
      <Select
        {...props}
        showSearch={showSearch}
        filterOption={showSearch ? filterOption as SelectProps['filterOption'] : undefined}
        ref={ref}
        allowClear={props.allowClear ?? true}
        className={cn('w-full', className)}
        options={Array.from({ length: 12 }).map((_, index) => ({
          label: `${index + 1}`,
          value: index + 1,
        }))}
      />
    </AntdFormItem>
  )
})